import { faCloudUploadAlt, faRedo, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { Button } from "../../App.styles";
import ConfirmationModal from "../ConfirmationModal";
import Spinner from '../Spinner';
import useImageThumbnail from "../ImageThumbnail/useImageThumbnail";
import ImageThumbnailLoader from "../ImageThumbnail/ImageThumbnailLoader";
import { alertState } from "../AlertDisplay/AlertDisplay.state";
import { getErrorMessage } from "../../lib/error";
import { useUploadImageMutation } from "./NubImageUpload.state";
import {
    ActionWrapper,
    FileWrapper,
    ImageUploadWrapper,
    ImageContainer,
    ImageBody,
    UploadBody,
    UploadBtn,
    ImageLoader,
    ImagePreviewWrapper,
    ImagePlaceholder,
    ImageActionButton,
    ImageActionButtons,
    HiddenImageProbe,
    NubsItem,
} from "./NubImageUpload.style";
import { acceptedImages } from "../../consts/accepted-files";
import { toAssetUrl } from "../../lib/url";
import ModalGeneratePhoto from "../ModalGeneratePhoto";

const keyMap = {
    photo: 'standard',
    arrival_image: 'arrival',
};

const translateKey = (key) => {
    switch (key) {
        case "ya":
            return "correct";
        case "naw":
            return "incorrect";
        case "challengesenabled":
            return "challenges";
        case "streamenabled":
            return "stream";
        case "leaderboardenabled":
            return "leaderboard";
        case "npcsenabled":
            return "npcs";
        case "reelsenabled":
            return "reels";
        case "rewardsenabled":
            return "rewards";
        case "joinenabled":
            return "join";
        case "notificationsenabled":
            return "notifications";
        case "helpenabled":
            return "help";
        case "customenabled":
            return "custom";
        case "overlay":
            return "sticker";
        case "tour_1":
            return 0;
        case "tour_2":
            return 1;
        case "tour_3":
            return 2;
        case "tour_4":
            return 3;
        default:
            return key;
    }
};

const findLegacyImageUrl = (collection, key, type) => {
    const assets = collection.assets;

    let asset = (assets?.[`${type}s`] || []).find(
        (assetItem) => assetItem.name === key && (assetItem.urls?.png || assetItem.url)
    );

    if (!asset) {
        asset = collection?.[type];
    }

    return asset?.[key]?.url ||
        asset?.urls?.png ||
        asset?.url ||
        null;
};

const toFileArray = (files) => {
    if (!files) {
        return [];
    }

    return Array.from(files).filter(Boolean);
};

/**
 * Snapshot selected files and cap them to remaining upload slots.
 * Single-image fields replace the current image instead of skipping when full.
 * @param {FileList|File[]} files
 * @param {{ maxImages: number, currentCount: number }} options
 * @returns {File[]}
 */
export const getFilesToUpload = (files, { maxImages, currentCount }) => {
    const selectedFiles = toFileArray(files);
    if (selectedFiles.length === 0) {
        return [];
    }

    if (maxImages === 1) {
        return selectedFiles.slice(0, 1);
    }

    if (maxImages > 0) {
        const remainingSlots = Math.max(maxImages - currentCount, 0);
        return selectedFiles.slice(0, remainingSlots);
    }

    return selectedFiles;
};

/**
 * File input ids must stay unique per field, matching `useImageUpload`.
 * Branding collections have no `id`, so a shared fallback like
 * `new-upload-undefined` makes every Select File label target the first
 * input in the DOM (logo on the branding form).
 * @param {{ name: string, type?: string, id?: string|number }} params
 * @returns {string}
 */
export const getFileInputId = ({ name, type = "photo", id = "" }) =>
    `${name}-${type}-${id}-upload`;

/**
 * Deleted previews must hide immediately, matching `useImageUpload` `clear()`.
 * Branding still exposes the previous URL on `collection.watermark` / `photo`
 * after a 200, so collection-derived lists cannot be the only source of truth.
 * @param {string[]} imageUrls
 * @param {string[]} removedUrls
 * @returns {string[]}
 */
export const getVisibleImageUrls = (imageUrls, removedUrls) =>
    imageUrls.filter((url) => url && !removedUrls.includes(url));

/**
 * Single-image fields replace the current image. While AI is generating, the
 * old preview must not keep occupying the only slot, or the loading nub never
 * appears.
 * @param {{ pendingGeneratedImages: number, maxImages: number, currentCount: number }} options
 * @returns {number}
 */
export const getPendingGeneratedSlots = ({ pendingGeneratedImages, maxImages, currentCount }) => {
    if (pendingGeneratedImages <= 0) {
        return 0;
    }

    if (maxImages <= 0) {
        return pendingGeneratedImages;
    }

    const replacingExisting = maxImages === 1 && currentCount > 0;
    const occupiedCount = replacingExisting ? 0 : currentCount;

    return Math.min(pendingGeneratedImages, Math.max(maxImages - occupiedCount, 0));
};

/**
 * Hide the current preview on single-image fields while a replacement AI image
 * is generating or while that replacement failed, so the loading/error nub is
 * shown instead of the old image.
 * @param {string[]} visibleUrls
 * @param {{ maxImages: number, pendingGeneratedImages: number, failedGenerationCount?: number }} options
 * @returns {string[]}
 */
export const getDisplayImageUrls = (visibleUrls, { maxImages, pendingGeneratedImages, failedGenerationCount = 0 }) => {
    if (maxImages === 1 && (pendingGeneratedImages > 0 || failedGenerationCount > 0)) {
        return [];
    }

    return visibleUrls;
};

/**
 * Renders a nub preview. For newly generated AI images, the generating
 * placeholder stays up until the thumbnail actually loads. The loader must
 * remain mounted (offscreen) or `loaded` never flips and the spinner sticks.
 */
const NubImage = ({ url, isUploading, containerRef, stickerify = false, showGeneratingWhenProcessing = false, onResolved }) => {
    const { t } = useTranslation();
    // Convert heic, dng, webp to jpg
    const imageUrl = (url || '').replace(/\.(heic|dng|webp)$/, '.jpg');
    const hookProps = useImageThumbnail({ thumb: imageUrl, isLoading: isUploading });
    const [imageLoaded] = hookProps.loadedState;
    const showTransparencyPattern = stickerify && !hookProps.mediaIsProcessing;
    const isWaitingForGeneratedImage = showGeneratingWhenProcessing && !imageLoaded;
    const imageThumbnailLoaderProps = {
        isLoading: isUploading,
        className: 'nubs__image',
        width: 'full',
        height: 'full',
        src: imageUrl,
        alt: '',
        ...hookProps,
    };

    useEffect(() => {
        if (showGeneratingWhenProcessing && imageLoaded) {
            onResolved && onResolved();
        }
    }, [imageLoaded, onResolved, showGeneratingWhenProcessing]);

    const imageLoader = <ImageThumbnailLoader {...imageThumbnailLoaderProps} />;

    if (isWaitingForGeneratedImage) {
        return (
            <>
                <HiddenImageProbe aria-hidden="true">
                    {imageLoader}
                </HiddenImageProbe>
                <ImagePlaceholder
                    className="align-items-center justify-content-center text-center p-3 text-muted"
                    container={containerRef?.current}
                >
                    <div>
                        <Spinner />
                        <small className="d-block mt-2">
                            {t('Generating in the background.')}
                            <br />
                            {t('This can take up to 5 minutes.')}
                        </small>
                    </div>
                </ImagePlaceholder>
            </>
        );
    }

    return (
        <NubsItem container={containerRef?.current} isImage={true} stickerify={showTransparencyPattern}>
            {imageLoader}
        </NubsItem>
    );
};

const NubImageUpload = ({
    label,
    name,
    type = "photo",
    standalone = false,
    useQuery,
    maxImages,
    withAi = true,
    stickerify = false,
    storageMode = "field",
    assetName,
    mediaPrefix,
}) => {
    const { t } = useTranslation();
    const setAlert = useSetRecoilState(alertState);
    const [showModal, setShowModal] = useState(false);
    const [retryPrompt, setRetryPrompt] = useState("");
    const [pendingGeneratedImages, setPendingGeneratedImages] = useState(0);
    const [generatedUrls, setGeneratedUrls] = useState([]);
    const [generationFailures, setGenerationFailures] = useState([]);
    const [removedUrls, setRemovedUrls] = useState([]);
    const [collection, setCollection] = useQuery();
    const { id = "" } = collection || {};
    const inputFileRef = useRef(null);
    const containerRef = useRef(null);
    const retryFailureIdRef = useRef(null);
    const generationFailureIdRef = useRef(0);
    const effectiveMaxImages = maxImages ?? (storageMode === "legacy" ? 1 : 9);
    
    const handleFileLabel = (e) => {
        if (e.target !== e.currentTarget || e.key !== "Enter" || inputFileRef.current === null) return;
        inputFileRef.current.click();
    };

    const {
        isLoading: isUploading,
        isGenerating,
        mutateAsync: uploadImage,
        createAiImage,
        clearMedia,
    } = useUploadImageMutation({
        useQuery,
        name,
        type,
        standalone,
        stickerify,
        storageMode,
        assetName,
        mediaPrefix,
        replaceExisting: effectiveMaxImages === 1,
    });

    const showUploadError = (error, actionContext) => {
        setAlert({
            type: "error",
            message: getErrorMessage(error, actionContext),
            closable: true,
        });
    };

    // Parse comma-delimited image URLs
    const imageUrls = useMemo(() => {
        if (storageMode === "asset") {
            const assetUrl = (collection?.assets?.photos || [])
                .find(asset => asset.name === assetName)?.url;
            return assetUrl ? [assetUrl] : [];
        }

        if (storageMode === "legacy") {
            const key = keyMap[name] || name;
            const imageUrl = findLegacyImageUrl(collection, translateKey(key), type);
            return imageUrl ? [imageUrl] : [];
        }

        const value = collection?.[name];
        if (!value) return [];
        return value.split(',')
            // filter out invalid URLs
            .filter(url => url.trim())
            // reverse the array so the most recent images are at the left
            .reverse();
    }, [assetName, collection, name, storageMode, type]);

    const visibleUrls = useMemo(
        () => getVisibleImageUrls(imageUrls, removedUrls),
        [imageUrls, removedUrls]
    );
    const displayUrls = useMemo(
        () => getDisplayImageUrls(visibleUrls, {
            maxImages: effectiveMaxImages,
            pendingGeneratedImages,
            failedGenerationCount: generationFailures.length,
        }),
        [effectiveMaxImages, generationFailures.length, pendingGeneratedImages, visibleUrls]
    );

    const upload = async (files) => {
        const filesToUpload = getFilesToUpload(files, {
            maxImages: effectiveMaxImages,
            currentCount: visibleUrls.length,
        });
        if (filesToUpload.length === 0) {
            return;
        }

        try {
            for (const file of filesToUpload) {
                const ext = `.${file.name.split('.').pop().toLowerCase()}`;
                if (!acceptedImages.includes(ext)) {
                    continue;
                }
                await uploadImage(file);
            }
            setGenerationFailures([]);
        } catch (error) {
            showUploadError(error, 'uploading your image');
        }
    };

    const onDelete = async (urlToDelete) => {
        setRemovedUrls((urls) => (urls.includes(urlToDelete) ? urls : [...urls, urlToDelete]));

        try {
            if (storageMode === "field") {
                const updatedUrls = imageUrls.filter(url => url !== urlToDelete);
                await setCollection({
                    [name]: updatedUrls.join(','),
                });
                return;
            }

            await clearMedia();
        } catch (error) {
            setRemovedUrls((urls) => urls.filter(url => url !== urlToDelete));
            showUploadError(error, 'deleting your image');
        }
    };

    const openGenerateModal = (prompt = "", failureId = null) => {
        retryFailureIdRef.current = failureId;
        setRetryPrompt(prompt);
        setShowModal(true);
    };

    const closeGenerateModal = () => {
        retryFailureIdRef.current = null;
        setRetryPrompt("");
        setShowModal(false);
    };

    const retryFailedGeneration = (failure) => {
        openGenerateModal(failure.prompt, failure.id);
    };

    const createAiImageInBackground = async (prompt) => {
        if (!prompt) return;

        const retryId = retryFailureIdRef.current;
        retryFailureIdRef.current = null;
        setGenerationFailures((failures) => {
            if (retryId) {
                return failures.filter((failure) => failure.id !== retryId);
            }

            return effectiveMaxImages === 1 ? [] : failures;
        });
        setPendingGeneratedImages((count) => count + 1);

        const actionContext = stickerify ? 'generating an AI sticker' : 'generating an AI photo';

        try {
            const generatedMedia = await createAiImage(prompt);
            if (generatedMedia?.mediaUrl) {
                setGeneratedUrls((urls) => [...urls, generatedMedia.mediaUrl]);
            }
            setPendingGeneratedImages((count) => Math.max(count - 1, 0));
        } catch (error) {
            const message = getErrorMessage(error, actionContext);
            generationFailureIdRef.current += 1;
            setPendingGeneratedImages((count) => Math.max(count - 1, 0));
            setGenerationFailures((failures) => [
                ...failures,
                {
                    id: generationFailureIdRef.current,
                    prompt,
                    message,
                },
            ]);
            showUploadError(error, actionContext);
        }
    };

    const handleGeneratedImageResolved = useCallback((url) => {
        setGeneratedUrls((urls) => urls.filter(generatedUrl => generatedUrl !== url));
    }, []);

    const placeholderLimit = effectiveMaxImages || 5;
    const pendingGeneratedSlots = getPendingGeneratedSlots({
        pendingGeneratedImages,
        maxImages: effectiveMaxImages,
        currentCount: visibleUrls.length,
    });
    const emptyPlaceholderCount = Math.max(
        placeholderLimit - displayUrls.length - pendingGeneratedSlots - generationFailures.length,
        0
    );
    const fileId = getFileInputId({ name, type, id });
    const isMultiAtCapacity = effectiveMaxImages > 1
        && visibleUrls.length + pendingGeneratedSlots + generationFailures.length >= effectiveMaxImages;
    const isDisabled = isUploading || pendingGeneratedSlots > 0 || isMultiAtCapacity;

    return (
        <ImageUploadWrapper className="form-outline">
            {label && <span className="form-label">{label}</span>}
            <ImageContainer ref={containerRef}>
                <ImageBody className="nubs__body" container={containerRef?.current}>
                    <UploadBody isDisabled={isDisabled} className="p-4" container={containerRef?.current} htmlFor={fileId} tabIndex={0} onKeyUp={handleFileLabel}>
                        <FontAwesomeIcon className="mb-4" icon={faCloudUploadAlt} />
                        <ActionWrapper>
                            <UploadBtn>
                                <FileWrapper>
                                    <input
                                        accept={acceptedImages.join(',')}
                                        className="form-control"
                                        id={fileId}
                                        type="file"
                                        multiple={effectiveMaxImages > 1 || effectiveMaxImages === 0}
                                        disabled={isDisabled}
                                        onChange={(evt) => {
                                            const selectedFiles = toFileArray(evt.target.files);
                                            evt.target.value = '';
                                            upload(selectedFiles);
                                        }}
                                        ref={inputFileRef}
                                    />
                                </FileWrapper>
                                <Button disabled={isDisabled || isUploading} tag="span" color="primary" className="btn--flat">
                                    {t('Select File')}
                                </Button>
                            </UploadBtn>
                            {
                                withAi
                                    ? (
                                        <Button
                                            disabled={isDisabled || isUploading}
                                            onClick={() => openGenerateModal()}
                                            className="btn--flat"
                                            color="primary"
                                        >
                                            {stickerify ? t('AI Generated Sticker') : t('AI Generated Photo')}
                                        </Button>
                                    )
                                    : null
                            }
                        </ActionWrapper>
                        <ModalGeneratePhoto
                            show={showModal}
                            onDismiss={closeGenerateModal}
                            loading={isGenerating}
                            createAiPhoto={createAiImageInBackground}
                            stickerify={stickerify}
                            initialPrompt={retryPrompt}
                        />
                        {displayUrls.length > 0 && (
                            <small className="text-muted">
                                {displayUrls.length} / {placeholderLimit}
                            </small>
                        )}
                        {isMultiAtCapacity && (
                            <small className="text-muted">
                                {t('Delete some images if you want to add more.')}
                            </small>
                        )}
                    </UploadBody>
                    {Array.from({ length: pendingGeneratedSlots }).map((_, index) => (
                        <ImagePlaceholder
                            key={`image-generating-placeholder-${index}`}
                            className="align-items-center justify-content-center text-center p-3 text-muted"
                            container={containerRef?.current}
                        >
                            <div>
                                <Spinner />
                                <small className="d-block mt-2">
                                    {t('Generating in the background.')}
                                    <br />
                                    {t('This can take up to 5 minutes.')}
                                </small>
                            </div>
                        </ImagePlaceholder>
                    ))}
                    {generationFailures.map((failure) => (
                        <ImagePlaceholder
                            key={`image-generation-error-${failure.id}`}
                            isError
                            className="align-items-center justify-content-center text-center"
                            container={containerRef?.current}
                        >
                            <small>{failure.message}</small>
                            <Button
                                type="button"
                                color="danger"
                                className="btn--flat"
                                onClick={() => retryFailedGeneration(failure)}
                            >
                                <FontAwesomeIcon icon={faRedo} /> {t('Retry')}
                            </Button>
                        </ImagePlaceholder>
                    ))}
                    {displayUrls.map((url, index) => (
                        <ImagePreviewWrapper key={`${url}-${index}`} isLoading={generatedUrls.includes(url)}>
                            <ImageActionButtons className="image-action__buttons">
                                <ConfirmationModal
                                    title={t("Confirm Deletion")}
                                    body={t("Are you sure you wish to delete?")}
                                    confirmLabel={t("Delete")}
                                    confirmAction={() => onDelete(url)}
                                >
                                    <ImageActionButton 
                                        title={t(`Delete Image`)} 
                                        color="link" 
                                        tabIndex={-1} 
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} />
                                    </ImageActionButton>
                                </ConfirmationModal>
                            </ImageActionButtons>
                            <NubImage
                                url={toAssetUrl(url)}
                                isUploading={false}
                                containerRef={containerRef}
                                stickerify={stickerify}
                                showGeneratingWhenProcessing={generatedUrls.includes(url)}
                                onResolved={() => handleGeneratedImageResolved(url)}
                            />
                        </ImagePreviewWrapper>
                    ))}
                    {Array.from({ length: emptyPlaceholderCount }).map((_, index) => (
                        <ImagePlaceholder
                            key={`image-placeholder-${index}`}
                            container={containerRef?.current}
                        >
                            <span>{t('Content will appear here')}</span>
                        </ImagePlaceholder>
                    ))}
                </ImageBody>
            </ImageContainer>
            <ImageLoader isLoading={isUploading}>
                <Spinner />
            </ImageLoader>
        </ImageUploadWrapper>
    );
};

NubImageUpload.displayName = 'NubImageUpload';
NubImageUpload.requiresFullRowInfo = true;

export default NubImageUpload;
